import * as Sentry from '@sentry/vue'
import { ref, watch } from 'vue'
import {
  NavigationGuardNext,
  RouteLocationNormalized,
  onBeforeRouteLeave,
  onBeforeRouteUpdate,
  useRoute,
} from 'vue-router'

import type { GetLeaveBlock, LeaveBlock } from '@/common/interfaces/UnsavedChangesInterfaces'

/* eslint-disable no-unused-vars */ // param names in these callback signatures are documentation
export interface UseUnsavedChangesGuardOptions {
  hasUpdates: () => boolean
  withinValidRoutes: (to: RouteLocationNormalized, from: RouteLocationNormalized) => boolean
  // Consulted only once `withinValidRoutes` has said no, so a rule guarding something mounted in a
  // *child* route can't rely on this.
  getLeaveBlock?: GetLeaveBlock
}
type Resolve = (shouldProceed: boolean) => void
/* eslint-enable no-unused-vars */

/**
 * The route-leave protocol behind `UnsavedChangesDialog`: one leave guard, one update guard, and the
 * promise carrying the answer back to the router. What the prompt says stays in the component.
 */
export function useUnsavedChangesGuard(options: UseUnsavedChangesGuardOptions) {
  const visible = ref<boolean>(false)
  const resolve = ref<null | Resolve>(null)
  // Whether a navigation is still waiting on `resolve`. Tracked separately so `resolve` can stay put
  // once used, and so no second answer can reach a router that already got one.
  const isDecisionPending = ref<boolean>(false)
  // A snapshot of the rule's answer, not the rule itself: a block raised while a save/discard prompt
  // is already open must not swap the footer under a navigation nobody has answered yet.
  const activeLeaveBlock = ref<LeaveBlock | null>(null)
  // Set once the venue has answered, so the fresh navigation a redirect produces doesn't ask them the
  // same question again.
  const isNavigating = ref<boolean>(false)

  // A landed navigation is the answer being spent. Watched rather than hooked onto the router so a
  // dialog mounted in a layout — which outlives the navigation it just allowed — guards the next one.
  const route = useRoute()
  watch(
    () => route.fullPath,
    function clearAnsweredDecision(): void {
      isNavigating.value = false
    },
  )

  function openModal(): Promise<boolean> {
    // A second attempt supersedes the one still waiting — otherwise that first navigation is never
    // answered and its promise leaks.
    settleNavigation(false)
    visible.value = true
    isDecisionPending.value = true
    return new Promise<boolean>(res => (resolve.value = res))
  }

  // The only writer of `isNavigating`: it records the venue's answer, and a refused leave is not one.
  function settleNavigation(shouldProceed: boolean): void {
    if (!isDecisionPending.value) return
    isDecisionPending.value = false
    isNavigating.value = shouldProceed
    resolve.value?.(shouldProceed)
  }

  function closePrompt(): void {
    visible.value = false
    activeLeaveBlock.value = null
  }

  async function readLeaveBlock(): Promise<LeaveBlock | null> {
    try {
      return (await options.getLeaveBlock?.()) ?? null
    } catch (error) {
      // A rule that cannot answer must not imprison the venue; the unsaved-changes half still applies.
      Sentry.captureException(error)
      return null
    }
  }

  async function preventNavigationWhenUnsavedChanges(
    to: RouteLocationNormalized,
    from: RouteLocationNormalized,
    next: NavigationGuardNext,
  ): Promise<void> {
    if (isNavigating.value || options.withinValidRoutes(to, from)) return next()
    // Written on every decision, not only the refusals: a block left over from a previous attempt
    // would dress an ordinary save-or-discard prompt up as a refusal.
    const leaveBlock = await readLeaveBlock()
    activeLeaveBlock.value = leaveBlock
    if (leaveBlock) {
      // Refused before the venue is asked: nothing they could answer changes the outcome. A refusal
      // supersedes whatever was still waiting, same as `openModal` — a decision left pending points at
      // a resolver nobody awaits, and the next answer would record a leave that never happened.
      settleNavigation(false)
      visible.value = true
      return next(false)
    }
    if (!options.hasUpdates()) return next()
    next(await openModal())
  }
  onBeforeRouteLeave(preventNavigationWhenUnsavedChanges)
  onBeforeRouteUpdate(preventNavigationWhenUnsavedChanges)

  return {
    visible,
    resolve,
    isDecisionPending,
    activeLeaveBlock,
    isNavigating,
    openModal,
    settleNavigation,
    closePrompt,
    preventNavigationWhenUnsavedChanges,
  }
}
